import cx from "classnames";
import { Button } from "components/Button";
import { Notification } from "components/common/Notification/Notification";
import { PriceWithLoader } from "components/common/PriceWithLoader/PriceWithLoader";
import { CustomDatoImage } from "components/CustomDatoImage/CustomDatoImage";
import { CustomSelect } from "components/CustomSelect/CustomSelect";
import { CylinderCompatibilityDialog } from "components/GasExchange/CylinderCompatibilityDialog";
import { BrandFeatureIconSvg } from "components/icons/BrandFeatureIconSvg";
import { Spinner } from "components/spinner/Spinner";
import { StarReviews } from "components/Yotpo/StarReviews";
import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import { useAddCylinderToCart } from "hooks/CylinderDetails/useAddCylinderToCart";
import { useGetPepdirectCylinders } from "hooks/CylinderDetails/useGetPepdirectCylinders";
import { FC, useContext, useMemo, useState } from "react";
import { StructuredText } from "react-datocms/structured-text";
import { ProductBySlugQuery } from "services/datocms/generated";
import { ItemByGtinQuery } from "services/graphql/generated";
import { colors } from "styles/variables";
import { ProductTextDescription } from "../ProductTextDescription/ProductTextDescription";
import { CylinderType, DatoCylinderType } from "./CylinderType";
import st from "./ProductDetailsCylinderNew.module.scss";

interface ProductDetailsCylinderV2Props {
  datoProduct: ProductBySlugQuery["product"] | null;
  pepDirectProduct: ItemByGtinQuery["itemByGtin"] | null;
}

export const ProductDetailsCylinderNew: FC<ProductDetailsCylinderV2Props> = ({
  datoProduct,
  pepDirectProduct,
}) => {
  const [selectedCylinderType, setSelectedCylinderType] =
    useState<DatoCylinderType | null>(
      () => datoProduct?.cylinderTypes[0] ?? null,
    );
  const [showCompatilityModal, setShowCompatilityModal] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(
    () => datoProduct?.cylinderQuantityOptions?.[0]?.amount || null,
  );

  const {
    selectCylinderType = "Select cylinder type",
    selectOrderQuantity = "Select order quantity",
    cylinderCompatibleWithTheFollowing = "Cylinder compatible with the following",
    whichCylinderDoINeed = "Which cylinder do I need?",
    addToCart = "Add to cart",
    comingSoon = "Coming soon",
    returnEmptyCylinder = "Return {value} cylinders",
    howManyDoYouNeed = "How many do you need?",
  } = useTranslationContext();
  const { formatPriceByLocale } = useContext(LocalizationContext);

  const {
    allPepDirectCylinders,
    query: { loading, data },
  } = useGetPepdirectCylinders(datoProduct?.cylinderTypes);
  const { handleAddCylinderToCart, cartLoading } = useAddCylinderToCart(
    data,
    datoProduct?.category?.analyticsCategoryName,
  );

  const selectedCylinderData = allPepDirectCylinders?.find(
    (item) => item.gtin === selectedCylinderType?.gtin,
  );

  const quantityOptions = useMemo(() => {
    return datoProduct?.cylinderQuantityOptions?.map((option) => ({
      value: option.amount,
      label: (
        <div>
          {`${option.amount} ${option.extraText}`}{" "}
          {option.promotionalText && (
            <span className={st.promotionalText}>{option.promotionalText}</span>
          )}
        </div>
      ),
      "bi-type": "Co2 Cylinder - Order quantity",
      "bi-id": `${option.amount} Cylinder`,
    }));
  }, [datoProduct?.cylinderQuantityOptions]);

  const outOfStock =
    selectedCylinderData &&
    (selectedCylinderData?.isOutOfStock || !selectedCylinderData?.inventoryQty);

  const currentPrice =
    (selectedQuantity &&
      selectedCylinderData?.price &&
      selectedQuantity * selectedCylinderData?.price) ||
    0;
  const formattedPrice = formatPriceByLocale(currentPrice);

  const renderButtonText = () => {
    if (outOfStock) {
      return comingSoon;
    }

    if (cartLoading || loading) {
      return <Spinner color={colors.white} dotSize="10px" />;
    }

    if (selectedQuantity) {
      return (
        <span>
          {addToCart} <span className={st.buttonDot}>•</span> {formattedPrice}
        </span>
      );
    } else {
      return addToCart;
    }
  };

  const isButtonDisabled = !selectedQuantity || outOfStock || cartLoading;

  return (
    <div className={cx("container", st.cylinderContainer)}>
      <div className={st.selectedCylinderImageDesktop}>
        {selectedCylinderType?.image?.responsiveImage && (
          <CustomDatoImage
            data={selectedCylinderType?.image?.responsiveImage}
          />
        )}
      </div>
      <div className={st.cylinderPurchase}>
        <div className={st.cylinderHeader}>
          <h1 className={st.title}>{selectedCylinderType?.title}</h1>
          <PriceWithLoader height={40} width={150} loading={loading}>
            <div className={st.price}>
              {formatPriceByLocale(
                allPepDirectCylinders?.find(
                  (item) => item.gtin === selectedCylinderType?.gtin,
                )?.price,
              )}
            </div>
          </PriceWithLoader>
          <div className={st.compatibleWithText}>
            {cylinderCompatibleWithTheFollowing}:{" "}
            {selectedCylinderType?.compatibleMachines
              .map((item) => item.title)
              .join(", ")}
          </div>
          <StarReviews gtin={pepDirectProduct?.gtin} />
        </div>
        <div className={st.selectedCylinderImageMobile}>
          {selectedCylinderType?.image?.responsiveImage && (
            <CustomDatoImage
              data={selectedCylinderType?.image?.responsiveImage}
            />
          )}
        </div>
        <div className={st.cylinderSelect}>
          <div className={st.cylinderSelectInfo}>
            <div className={st.selectCylinder}>{selectCylinderType}</div>
            <div
              className={st.whichCylinderText}
              onClick={() => setShowCompatilityModal(true)}
              bi-type="Co2 Cylinder - Select a cylinder"
              bi-id="View compatibility"
            >
              {whichCylinderDoINeed}
            </div>
          </div>
          <div className={st.cylinderTypeSelect}>
            {datoProduct?.cylinderTypes?.map((cylinderType, index) => (
              <CylinderType
                key={cylinderType.gtin}
                cylinderType={cylinderType}
                isSelected={selectedCylinderType?.gtin === cylinderType.gtin}
                setSelectedCylinderType={setSelectedCylinderType}
                index={index}
              />
            ))}
          </div>
        </div>
        <div className={st.cylinderQuantity}>
          <div className={st.cylinderQuantityInfo}>{howManyDoYouNeed}</div>
          <CustomSelect
            onChange={(selected) => setSelectedQuantity(selected?.value)}
            options={quantityOptions}
            placeholder={selectOrderQuantity}
            className={st.quantitySelect}
            defaultValue={quantityOptions?.[0]}
          />
          {datoProduct?.cylinderSelectionInfo && (
            <div className={st.cylinderSelectionText}>
              {datoProduct.cylinderSelectionInfo}
            </div>
          )}
        </div>
        {selectedQuantity && (
          <Notification
            type="info"
            fullWidth
            title={returnEmptyCylinder?.replace(
              "{value}",
              selectedQuantity.toString(),
            )}
            subText={
              datoProduct?.returnPolicy?.policy && (
                <div className={st.returnPolicy}>
                  <StructuredText data={datoProduct?.returnPolicy?.policy} />
                </div>
              )
            }
          />
        )}
        <div className={st.buttonContainer}>
          <Button
            disabled={isButtonDisabled}
            fullWidth
            bi-type={"Co2 Cylinder - Add to cart"}
            bi-id={`Add to cart for ${formattedPrice}`}
            onClick={() =>
              handleAddCylinderToCart(
                selectedCylinderData?.id,
                selectedQuantity ?? 0,
              )
            }
          >
            {renderButtonText()}
          </Button>
        </div>
      </div>
      <div>
        {selectedCylinderType?.blueBoxText && (
          <div className={st.calloutContainer}>
            <div className={st.callout}>
              <div>
                <BrandFeatureIconSvg />
              </div>
              <div className={st.calloutText}>
                {selectedCylinderType?.blueBoxText}
              </div>
            </div>
          </div>
        )}
        {selectedCylinderType?.cylinderTextDescription && (
          <ProductTextDescription
            description={selectedCylinderType?.cylinderTextDescription}
          />
        )}
      </div>
      {showCompatilityModal && selectedCylinderType?.compatibleMachines && (
        <CylinderCompatibilityDialog
          header={cylinderCompatibleWithTheFollowing}
          machines={selectedCylinderType?.compatibleMachines}
          onClose={() => setShowCompatilityModal(false)}
          onOkClick={() => setShowCompatilityModal(false)}
        />
      )}
    </div>
  );
};
